import { useCallback, useEffect, useRef, useState } from 'react';
import { ApolloError } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import {
  KommoSyncStatusFieldsFragment,
  KommoUserFieldsFragment,
  useKommoSyncStateQuery,
  useKommoUserListQuery,
  useRetryLeadNotesMutation,
  useSaveKommoUserMappingMutation,
  useSyncLeadsNowMutation,
} from 'core/graphql';
import { ToastTypes } from 'types';

import { usePageVisiblePoll } from '../Meta/usePageVisiblePoll';
import { ILeadError, useLeadErrorText, useLeadsNotify } from './leadsFeedback';
import { KOMMO_SYNC_POLL_MS, KOMMO_SYNC_WATCH_MS } from './leadsModel';
import { useLeadPermissions } from './useLeadPermissions';

/**
 * ТЗ 0084 — вкладка Admin «Kommo»: состояние синхронизации, «Синхронизировать»,
 * очередь примечаний, сопоставление пользователей Kommo с сотрудниками ERP.
 */

export type IKommoSyncStatus = KommoSyncStatusFieldsFragment;
export type IKommoSyncRun = KommoSyncStatusFieldsFragment['runs'][number];
export type IKommoUser = KommoUserFieldsFragment;

const EMPTY_USERS: IKommoUser[] = [];

type RawErrors =
  | ReadonlyArray<{
      field?: string | null;
      code?: string | null;
      message?: string | null;
    } | null>
  | null
  | undefined;

const toErrors = (raw: RawErrors): ILeadError[] =>
  (raw ?? [])
    .filter((error): error is NonNullable<typeof error> => Boolean(error))
    .map((error) => ({
      field: error.field ?? '',
      code: error.code ?? '',
      message: error.message ?? '',
    }));

const crashError = (caught: unknown): ILeadError => ({
  field: '',
  code:
    caught instanceof ApolloError && caught.graphQLErrors.length > 0
      ? 'NO_ACCESS'
      : 'NETWORK',
  message: '',
});

/**
 * Состояние синхронизации. Опрос — пока прогон идёт или пока ждём прогон,
 * поставленный кнопкой (`watch`): задача уходит в Celery, и первые секунды
 * нового прогона в списке ещё нет. Ждём не дольше `KOMMO_SYNC_WATCH_MS` —
 * упавший воркер не должен держать опрос вечно.
 */
export const useKommoSyncStatus = (enabled = true) => {
  const { isAdmin } = useLeadPermissions();
  const skip = !isAdmin || !enabled;
  const {
    data,
    loading,
    error,
    refetch,
    startPolling,
    stopPolling,
  } = useKommoSyncStateQuery({
    skip,
    fetchPolicy: 'cache-and-network',
  });
  const status = data?.kommoSyncStatus ?? null;
  const latest = status?.runs[0] ?? null;
  const running = latest?.status === 'running';
  // Id последнего прогона на момент нажатия: новый прогон — другой id.
  const [watch, setWatch] = useState<{ since: string | null } | null>(null);
  const latestIdRef = useRef<string | null>(null);
  latestIdRef.current = latest?.id ?? null;
  const settled = Boolean(
    watch && latest && latest.id !== watch.since && latest.status !== 'running',
  );

  useEffect(() => {
    if (settled) setWatch(null);
  }, [settled]);

  useEffect(() => {
    if (!watch) return undefined;
    const timer = window.setTimeout(() => setWatch(null), KOMMO_SYNC_WATCH_MS);
    return () => window.clearTimeout(timer);
  }, [watch]);

  usePageVisiblePoll(
    startPolling,
    stopPolling,
    KOMMO_SYNC_POLL_MS,
    !skip && (Boolean(watch) || running),
  );

  const reload = useCallback(() => {
    refetch()?.catch(() => undefined);
  }, [refetch]);

  /** Начать ждать прогон, который только что поставили в очередь. */
  const watchNextRun = useCallback(() => {
    setWatch({ since: latestIdRef.current });
  }, []);

  return {
    status,
    /** Прогон идёт или поставлен и ещё не начался. */
    busy: running || Boolean(watch),
    loading: loading && !data,
    failed: Boolean(error) && !data,
    reload,
    watchNextRun,
  };
};

/** Пользователи Kommo и сотрудники ERP за ними. */
export const useKommoUsers = (enabled = true) => {
  const { isAdmin } = useLeadPermissions();
  const { data, loading, error, refetch } = useKommoUserListQuery({
    skip: !isAdmin || !enabled,
    fetchPolicy: 'cache-and-network',
  });
  const reload = useCallback(() => {
    refetch()?.catch(() => undefined);
  }, [refetch]);
  return {
    users: data?.kommoUsers ?? EMPTY_USERS,
    loading: loading && !data,
    failed: Boolean(error) && !data,
    reload,
  };
};

/** Действия вкладки «Kommo»: синхронизация, повтор примечаний, сопоставление. */
export const useKommoAdminActions = () => {
  const { t } = useTranslation();
  const notify = useLeadsNotify();
  const errorText = useLeadErrorText();
  const [syncMut, { loading: syncing }] = useSyncLeadsNowMutation();
  const [retryMut, { loading: retrying }] = useRetryLeadNotesMutation();
  const [mapMut, { loading: mapping }] = useSaveKommoUserMappingMutation();

  const fail = useCallback(
    (title: string, errors: ILeadError[]) => {
      notify(
        ToastTypes.error,
        title,
        errors.map((error) => errorText(error)).join('\n') || undefined,
      );
    },
    [errorText, notify],
  );

  /** Прогон в фоне: ответ — «поставлено в очередь», итог покажет опрос. */
  const syncNow = useCallback(
    async (full: boolean): Promise<boolean> => {
      try {
        const { data } = await syncMut({ variables: { full } });
        const payload = data?.syncKommoLeads;
        const errors = toErrors(payload?.customErrors);
        if (!payload?.success || errors.length || !payload.queued) {
          fail(t('leads.kommo.toast.syncFailed'), errors);
          return false;
        }
        notify(
          ToastTypes.success,
          t(full ? 'leads.kommo.toast.fullQueued' : 'leads.kommo.toast.queued'),
        );
        return true;
      } catch (caught) {
        fail(t('leads.kommo.toast.syncFailed'), [crashError(caught)]);
        return false;
      }
    },
    [fail, notify, syncMut, t],
  );

  const retryNotes = useCallback(async (): Promise<boolean> => {
    try {
      const { data } = await retryMut();
      const payload = data?.retryKommoNotes;
      const errors = toErrors(payload?.customErrors);
      if (!payload?.success || errors.length) {
        fail(t('leads.kommo.toast.retryFailed'), errors);
        return false;
      }
      notify(
        ToastTypes.success,
        t('leads.kommo.toast.retried', { n: payload.count ?? 0 }),
      );
      return true;
    } catch (caught) {
      fail(t('leads.kommo.toast.retryFailed'), [crashError(caught)]);
      return false;
    }
  }, [fail, notify, retryMut, t]);

  /**
   * Сотрудник ERP за пользователем Kommo. Выбор руками фиксирует выбор
   * (`locked`); снятый замок возвращает запись автоматике (по email).
   * Ответ — та же запись `KommoUserType:<id>`, таблица обновится сама.
   */
  const saveMapping = useCallback(
    async (
      kommoUserId: string,
      userId: string | null,
      locked: boolean,
    ): Promise<boolean> => {
      try {
        const { data } = await mapMut({
          variables: { kommoUserId, userId, locked },
        });
        const payload = data?.setKommoUserMapping;
        const errors = toErrors(payload?.customErrors);
        if (!payload?.success || errors.length) {
          fail(t('leads.kommo.toast.mappingFailed'), errors);
          return false;
        }
        notify(ToastTypes.success, t('leads.kommo.toast.mappingSaved'));
        return true;
      } catch (caught) {
        fail(t('leads.kommo.toast.mappingFailed'), [crashError(caught)]);
        return false;
      }
    },
    [fail, mapMut, notify, t],
  );

  return {
    syncNow,
    retryNotes,
    saveMapping,
    busy: { syncing, retrying, mapping },
  };
};
